import { Component } from '@angular/core';
import { HeaderComponent } from '../../shared/header/header.component';
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';
import { UserService } from '../../../services/user/user.service';
import { CommonModule } from '@angular/common';
import { NgxMaskDirective } from 'ngx-mask';
import { PatientService } from '../../../services/patient/patient.service';
import { ModalComponent } from '../../shared/modal/modal.component';
import { Router } from '@angular/router';
import { PasswordService } from '../../../services/password/password.service';
import { AttendanceCycleService } from '../../../services/attendance-cycle/attendance-cycle.service';
import { FinishPasswordDTO } from '../../../interfaces/finish-password-dto';
import { OpenAttendanceModalComponent } from '../../shared/open-attendance-modal/open-attendance-modal.component';

@Component({
  selector: 'app-link-patient',
  standalone: true,
  imports: [
    HeaderComponent,
    CommonModule,
    NgxMaskDirective,
    ModalComponent,
    OpenAttendanceModalComponent,
  ],
  templateUrl: './link-patient.component.html',
  styleUrl: './link-patient.component.scss',
})
export class LinkPatientComponent {
  userName = '';
  unitName = '';
  clinicId!: number;
  data: any;
  showCPFSearch = true;
  formattedCPF!: string;

  patients: any;
  pageNumber: number = 1;
  pageSize: number = 10;

  currentStep: number = 1;
  isCheckboxChecked: boolean = false;
  isLoading: boolean = false;

  searchMode: 'cpf' | 'name' | 'rne' | 'passport' = 'cpf';

  showButtons = false;
  isNextPageAvailable: boolean = false;
  scheduleSelected = false;
  openAttendanceModal: boolean = false;

  isModalOpen = false;
  isInfoModalOpen = false;
  modalTitle = '';
  modalMessage = '';

  schedulesData: any;
  scheduleId!: number;
  hasSchedule = false;
  hasSearched = false;
  passwordId!: number;
  patientId!: number;
  classification!: string;

  passwordData!: any;
  passwordSpecialty!: string;
  passwordCode!: string;

  finishPasswordData!: FinishPasswordDTO;

  constructor(
    private localStorageService: LocalStorageService,
    private userService: UserService,
    private patientService: PatientService,
    private router: Router,
    private passwordService: PasswordService,
    private attendanceCycleService: AttendanceCycleService,
  ) {
    this.userName = this.localStorageService.get('login');
    this.clinicId = this.localStorageService.get('currentUnit');
    this.unitName = this.localStorageService.get('unitName');
    this.getPasswordData();
  }

  async ngOnInit() {
    await this.getUserByLogin(this.userName);
    this.currentStep = 1;
  }

  async getPasswordData() {
    this.passwordData = await this.localStorageService.get('selectedPassword');
    this.passwordId = this.passwordData.id;
    this.passwordSpecialty = this.passwordData.specialty
      ? this.passwordData.specialty
      : 'Express';
    this.passwordCode = this.passwordData.dsPassword;
  }

  async search(type: 'cpf' | 'name' | 'rne' | 'passport') {
    this.isLoading = true;
    this.hasSearched = false;
    this.patients = [];
    this.pageNumber = 1;

    try {
      if (type === 'cpf') {
        const cpfInput = document.getElementById(
          'cpfSearch',
        ) as HTMLInputElement;
        const formattedCPF = this.cpfFormatted(cpfInput.value);

        this.localStorageService.set('patientDocument', formattedCPF);

        this.patients = await this.patientService.getPatientByCPF(formattedCPF);
        this.isLoading = false;
      } else if (type === 'name') {
        await this.loadPatients();
      } else {
        const textInput = document.getElementById(
          'textSearch',
        ) as HTMLInputElement;

        const filterType = type === 'rne' ? 0 : 1;

        const person = await this.patientService.getPersonByFilter({
          type: filterType,
          filter: textInput.value,
        });

        this.isLoading = false;

        this.patients = person ? [person] : [];
      }

      if (this.patients.length < this.pageSize) {
        this.isNextPageAvailable = false;
      }

      if (this.patients.length === 0) {
        this.hasSearched = true;
      }
    } catch (error) {
      this.isLoading = false;
      this.hasSearched = true;
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  }

  async loadPatients() {
    this.isLoading = true;
    const nameInput = document.getElementById('textSearch') as HTMLInputElement;

    try {
      const response = await this.patientService.getPatientByName(
        nameInput.value,
        this.pageNumber,
        this.pageSize,
      );
      this.patients = response;
      this.isLoading = false;
      if (this.patients.length == this.pageSize) {
        this.isNextPageAvailable = true;
      } else {
        this.isNextPageAvailable = false;
        this.isLoading = false;
      }
    } catch (error) {
      this.isLoading = false;
      console.error('Erro ao encontrar pacientes:', error);
    } finally {
      this.isLoading = false;
    }
  }

  setSearchMode(mode: 'cpf' | 'name' | 'rne' | 'passport') {
    this.searchMode = mode;
  }

  getPlaceholder(): string {
    switch (this.searchMode) {
      case 'rne':
        return 'RNE';
      case 'passport':
        return 'Passaporte';
      default:
        return 'Nome';
    }
  }

  nextPage() {
    this.pageNumber++;
    this.loadPatients();
  }

  previousPage() {
    if (this.pageNumber > 1) {
      this.pageNumber--;
      this.loadPatients();
    }
  }

  async selectPatient(patientId: number) {
    this.patientId = patientId;
    this.localStorageService.set('patientId', String(patientId));
    try {
      this.schedulesData = await this.patientService.getSchedulesByPatient(
        patientId,
        this.clinicId,
      );
      if (this.schedulesData.length != 0) this.hasSchedule = true;
      this.openModal('schedule');
    } catch (error) {}
  }

  selectSchedule(
    scheduleId: number,
    scheduleClassification: string,
    specialtyName: string,
  ) {
    if (this.scheduleId === scheduleId) {
      this.scheduleId = 0;
      this.scheduleSelected = false;
    } else {
      const schedule = this.schedulesData.find(
        (p: { id: number }) => p.id === scheduleId,
      );
      this.scheduleId = scheduleId;
      this.localStorageService.set('scheduleId', scheduleId);
      this.localStorageService.set(
        'scheduleClassification',
        scheduleClassification,
      );
      this.localStorageService.set('inCaseToRemoveProcedure', specialtyName);
      this.scheduleSelected = true;
      this.classification = schedule?.classification;
    }
  }

  // Função criada para validar usuário/clínica a acessar feature de tela de atendimento
  validateToFeature(): boolean {
    let loggedUser = String(this.localStorageService.get('login')); // Se validação for por usuário
    let clinicAuth = Number(this.localStorageService.get('currentUnit')); // Se validação for por clínica
    let classification = String(
      this.localStorageService.get('scheduleClassification'),
    ); // Classificação do agendamento: EXAM || CONSULTATION

    const validUsers: string[] = ['KEVIN.P', 'GABRIEL.SM', 'HONORIO.V']; // Insira nesta lista os usuários autorizados
    const validClinicsForExam: number[] = [
      1004, 1007, 272, 273, 274, 1009, 279, 627, 241, 277, 1001, 280, 1002, 103,
      22,
    ]; // Insira aqui o ID da clínica autorizada para exames
    const validClinicsForConsultation: number[] = [
      1004, 1007, 272, 273, 274, 1009, 279, 627, 241, 277, 1001, 280, 1002, 103,
      22,
    ]; // Insira aqui o ID da clínica autorizada para consultas

    const userIsAllowed = validUsers.includes(loggedUser);
    const clinicIsAllowedForExam = validClinicsForExam.includes(clinicAuth);
    const clinicIsAllowedForConsultation =
      validClinicsForConsultation.includes(clinicAuth);

    // Regra 1: clínica autorizada para CONSULTATION
    if (clinicIsAllowedForConsultation && classification === 'CONSULTATION')
      return true;

    // Regra 2: usuário autorizado para CONSULTATION
    if (userIsAllowed && classification === 'CONSULTATION') return true;

    // Regra 3: clínica autorizada para EXAM
    if (clinicIsAllowedForExam && classification === 'EXAM') return true;

    // Regra 4: usuário autorizado para EXAM
    if (userIsAllowed && classification === 'EXAM') return true;

    return false;
  }

  async linkAndNextStep() {
    try {
      await this.passwordService.linkPassword(
        this.userName,
        0,
        this.passwordId,
        this.patientId,
        Number(this.clinicId),
        this.scheduleId,
        this.classification,
      );

      if (this.validateToFeature()) {
        this.openAttendanceModal = true;
      } else {
        this.currentStep++;
      }
    } catch (error) {
      console.log(error);
    } finally {
      //this.currentStep++;
    }
  }

  async finish() {
    try {
      await this.passwordService.finishPassword(
        this.userName,
        0,
        this.passwordId,
        Number(this.clinicId),
      );
    } catch (error) {
      console.log(error);
    } finally {
      try { await this.attendanceCycleService.finish('ATENDIDO'); } catch {}
      let removePassword = this.localStorageService.get('selectedPassword');
      if (removePassword.id == this.passwordId) {
        this.localStorageService.remove('selectedPassword');
      }
      this.backToDashboard();
    }
  }

  onCheckboxChange(event: Event) {
    const checkbox = event.target as HTMLInputElement;
    this.isCheckboxChecked = checkbox.checked;
  }

  cpfFormatted(cpf: string) {
    if (cpf) {
      return String(cpf.match(/\d/g)?.join(''));
    } else {
      return '';
    }
  }

  formattedTime(date: Date) {
    const data = new Date(date);
    const horas = data.getHours().toString().padStart(2, '0');
    const minutos = data.getMinutes().toString().padStart(2, '0');
    const formattedTime = `${horas}:${minutos}`;
    return formattedTime;
  }

  async getUserByLogin(login: string) {
    try {
      this.data = await this.userService.getUserByLogin(login);
    } catch (erro) {
      console.log('Erro ao buscar dados do paciente.');
    }
  }

  backToDashboard() {
    this.router.navigate(['/home']);
  }

  toggleSearch() {
    this.patients = [];
    this.showCPFSearch = !this.showCPFSearch;
  }

  openModal(type: string) {
    if (type === 'schedule') {
      this.currentStep = 1;
      this.isModalOpen = true;
    } else {
      this.isInfoModalOpen = true;
    }
  }

  closeModal(type: string) {
    type === 'schedule'
      ? (this.isModalOpen = false)
      : (this.isInfoModalOpen = false);
  }
}
